import { useMemo, type ReactNode } from 'react'
import { useRegionalEvents } from '../../hooks/useRegionalEvents'
import { useTrackView } from '../../hooks/useTrackView'
import { isEventActive } from '../../lib/formatDate'
import { getNow } from '../../lib/clock'
import { track } from '../../lib/analytics'
import type { Event, EventAttendanceStatus } from '../../types'
import { useForYou } from './useForYou'
import { ForYouSection } from './ForYouSection'

/**
 * Home's filler while the viewer has no plans: the top events in their region,
 * ranked for them. Mounted only in that state, so planners never pay for the
 * region's events on Home.
 */
export function HomeSuggestions({
  eventStatuses,
  signedInUid,
  action,
  renderCard,
}: {
  eventStatuses: Map<string, EventAttendanceStatus>
  signedInUid: string | null
  action: ReactNode
  renderCard: (event: Event, onOpen: () => void) => ReactNode
}) {
  const { events, isEverywhere, isNear, regionId, eventsPending, regionsLoading } = useRegionalEvents(eventStatuses)
  const now = getNow()
  const pool = useMemo(
    () => events.filter(e => !e.cancelled && isEventActive(e, now) && (isEverywhere || isNear(e))),
    [events, now, isEverywhere, isNear],
  )
  const ready = !eventsPending && !regionsLoading
  const forYou = useForYou(pool, signedInUid, ready, `${signedInUid ?? 'guest'}:${regionId}`)

  // Zero events in the viewer's region is the content-demand signal.
  useTrackView('near_you_empty', ready && !isEverywhere && pool.length === 0 ? { surface: 'home' } : null)
  if (ready && pool.length === 0) return null

  return (
    <ForYouSection
      events={forYou.events}
      personalized={forYou.personalized}
      loading={forYou.loading}
      action={action}
      renderCard={event =>
        renderCard(event, () =>
          track('featured_event_opened', {
            event_id: event.id,
            entry_point: forYou.personalized ? 'home_for_you' : 'home_popular',
          }),
        )
      }
    />
  )
}
